import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  recruitmentAPI,
  RecruitmentRound,
  RecruitmentState,
  RoundConfig,
  recruitmentError,
} from "../services/recruitmentAPI";
import RecruitmentRoundEditor from "./RecruitmentRoundEditor";

export default function DriveRecruitmentPanel({
  driveId,
  roundId,
  onRoundsChange,
}: {
  driveId: number;
  roundId?: number;
  onRoundsChange: (rounds: RecruitmentRound[]) => void;
}) {
  const [state, setState] = useState<RecruitmentState | null>(null);
  const [draft, setDraft] = useState<RoundConfig>();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await recruitmentAPI.get(driveId);
      setState(data);
      setDraft(data.config);
      setError("");
      setSelected(
        new Set(
          data.candidates
            .filter((c) => roundId && c.selected_round_ids.includes(roundId))
            .map((c) => c.candidate_id),
        ),
      );
      onRoundsChange(data.rounds.filter((r) => r.kind !== "assessment"));
    } catch (e) {
      setError(recruitmentError(e));
    }
  }, [driveId, roundId, onRoundsChange]);
  useEffect(() => {
    void load();
  }, [load]);
  const configure = async (config: RoundConfig) => {
    setBusy(true);
    try {
      try {
        await recruitmentAPI.configure(driveId, config);
      } catch (e: any) {
        if (
          e.response?.status === 409 &&
          String(e.response?.data?.detail).startsWith("Changing round")
        ) {
          if (
            !window.confirm(
              "Changing the configured rounds resets current interview selections for this drive. Earlier decisions remain in audit history. Continue?",
            )
          )
            return;
          await recruitmentAPI.configure(driveId, config, true);
        } else throw e;
      }
      toast.success("Recruitment configuration saved");
      await load();
    } catch (e) {
      toast.error(recruitmentError(e));
    } finally {
      setBusy(false);
    }
  };
  if (error)
    return (
      <div className="rounded border bg-white p-6">
        <p role="alert" className="text-red-600">
          {error}
        </p>
        <button onClick={() => void load()} className="mt-3 text-blue-600">
          Retry
        </button>
      </div>
    );
  if (!state || !draft)
    return (
      <p role="status" className="p-6">
        Loading recruitment records...
      </p>
    );
  const round = state.rounds.find((r) => r.round_id === roundId);
  const firstManaged = state.config.round_ids[1] === roundId;
  const cohort = state.candidates.filter(
    (c) =>
      roundId &&
      (c.eligible_round_ids.includes(roundId) ||
        c.selected_round_ids.includes(roundId)),
  );
  const visible = cohort.filter((c) =>
    `${c.name} ${c.email} ${c.college}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const emailTpo = async () => {
    setBusy(true);
    try {
      await recruitmentAPI.emailTpo(driveId);
      toast.success("Test registration email sent to TPO");
    } catch (e) {
      toast.error(recruitmentError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!roundId)
    return (
      <section className="space-y-5 rounded-xl border bg-white p-6">
        <h2 className="text-xl font-semibold">Configure Recruitment Rounds</h2>
        {state.drive.tpo_email_id && (
          <button
            disabled={busy}
            onClick={() => void emailTpo()}
            className="inline-block rounded bg-blue-50 px-4 py-2 text-blue-700 disabled:opacity-50"
          >
            Send Test Email to TPO
          </button>
        )}
        <RecruitmentRoundEditor
          value={draft}
          onChange={setDraft}
          disabled={busy}
        />
        <button
          disabled={busy}
          onClick={() => void configure(draft)}
          className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
        >
          Save Round Configuration
        </button>
      </section>
    );
  if (!round)
    return (
      <p className="p-6">
        This round is no longer configured. Choose another round tab.
      </p>
    );
  const save = async () => {
    setBusy(true);
    try {
      await recruitmentAPI.select(
        driveId,
        roundId,
        [...selected],
        state.config.revision,
      );
      toast.success("Candidate decisions saved");
      await load();
    } catch (e) {
      toast.error(recruitmentError(e));
    } finally {
      setBusy(false);
    }
  };
  const exportRows = () => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(
        visible.map((c) => ({
          Name: c.name,
          Email: c.email,
          College: c.college,
          "Assessment Score (%)": c.score,
          Selected: selected.has(c.candidate_id) ? "Yes" : "No",
        })),
      ),
      "Candidates",
    );
    XLSX.writeFile(
      workbook,
      `drive-${driveId}-${round.name.replace(/[^a-z0-9]/gi, "-")}.xlsx`,
    );
  };
  return (
    <section className="space-y-4 rounded-xl border bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{round.name}</h2>
          <p className="text-sm text-gray-500">
            {cohort.length} candidates · {selected.size} selected
          </p>
        </div>
        <button disabled={busy} onClick={() => { setBusy(true); void load().finally(() => setBusy(false)); }} className="rounded border px-4 py-2">Refresh Candidates</button>
        <button onClick={exportRows} className="rounded border px-4 py-2">
          Export Table
        </button>
      </div>
      {firstManaged && (
        <div className="flex flex-wrap items-center gap-3 rounded bg-blue-50 p-4">
          <label className="text-sm font-medium">
            Assessment cutoff (%){" "}
            <input
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={draft.cutoff}
              onChange={(e) =>
                setDraft({ ...draft, cutoff: Number(e.target.value) })
              }
              className="ml-2 w-24 rounded border p-2"
            />
          </label>
          <button
            disabled={busy}
            onClick={() =>
              void configure({ ...state.config, cutoff: draft.cutoff })
            }
            className="rounded bg-blue-600 px-3 py-2 text-white"
          >
            Apply Cutoff
          </button>
          <span className="text-sm">
            Only completed, graded assessments qualify. Saved promotions are
            retained when the cutoff changes.
          </span>
        </div>
      )}
      <label className="block text-sm">
        Search Candidates
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mt-1 w-full rounded border p-2"
          placeholder="Name, email or college"
        />
      </label>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3">
                <input
                  type="checkbox"
                  aria-label="Select all visible candidates"
                  disabled={busy || !visible.length}
                  checked={
                    !!visible.length &&
                    visible.every((c) => selected.has(c.candidate_id))
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelected((previous) => {
                      const next = new Set(previous);
                      visible.forEach((c) =>
                        checked
                          ? next.add(c.candidate_id)
                          : next.delete(c.candidate_id),
                      );
                      return next;
                    });
                  }}
                />
              </th>
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">College</th>
              <th className="p-3">Assessment Score</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => (
              <tr key={c.candidate_id} className="border-t">
                <td className="p-3">
                  <input
                    type="checkbox"
                    disabled={busy}
                    aria-label={`Select ${c.name}`}
                    checked={selected.has(c.candidate_id)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelected((previous) => {
                        const next = new Set(previous);
                        checked
                          ? next.add(c.candidate_id)
                          : next.delete(c.candidate_id);
                        return next;
                      });
                    }}
                  />
                </td>
                <td className="p-3">{c.name}</td>
                <td className="p-3">{c.email}</td>
                <td className="p-3">{c.college}</td>
                <td className="p-3">{c.score.toFixed(2)}%</td>
                <td className="p-3">
                  {!c.eligible_round_ids.includes(roundId) ? (
                    <span className="text-amber-700">
                      Previously promoted · review cutoff
                    </span>
                  ) : c.selected_round_ids.includes(roundId) ? (
                    "Selected"
                  ) : (
                    "Awaiting recruiter decision"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!visible.length && (
          <p className="p-5 text-gray-500">
            No candidates qualify for this round yet. Check the cutoff or clear
            candidates in the previous round.
          </p>
        )}
      </div>
      <button
        disabled={busy}
        onClick={() => void save()}
        className="rounded bg-green-600 px-4 py-2 text-white disabled:opacity-50"
      >
        {busy
          ? "Saving..."
          : round.kind === "joined"
            ? "Record Joined Candidates"
            : round.kind === "offer"
              ? "Record Offer Acceptances"
              : round.kind === "final"
                ? "Confirm Final Selections"
                : "Save & Move Selected to Next Round"}
      </button>
    </section>
  );
}
